import { useEffect, useMemo, useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CategoryIcon } from "@/lib/categoryIcons";
import { useIsPhone } from "@/lib/useMediaQuery";
import { CaretDown, CaretUp } from "@phosphor-icons/react";

// One row of tiles. The list arrives sorted by how often each category is
// used, so the three that cover most entries are the three at the front.
const COLLAPSED_COUNT = 3;

/**
 * Picking a category during data entry.
 *
 * On a pointer device a dropdown is the right control: compact, familiar, and
 * a mouse has no trouble with a 32px row. On a phone it is the wrong one. The
 * list opens as an overlay with its own scroll region inside a sheet that is
 * itself draggable, so the two gestures compete; the rows sit under the touch
 * target size; and most of the categories stay out of view behind a chevron.
 *
 * Tiles solve that, but twelve of them would fill the form, so only the most
 * used three show until asked. Whatever is already selected stays on screen
 * even when it is not one of the three — a picker that hides the current value
 * reads as though nothing is chosen.
 */
export default function CategoryPicker({ categories, value, onChange, kind }) {
  const phone = useIsPhone();
  const [expanded, setExpanded] = useState(false);

  // Switching between expense and income swaps the whole list, so the
  // disclosure goes back to its short form with it.
  useEffect(() => { setExpanded(false); }, [kind]);

  const visible = useMemo(() => {
    if (expanded) return categories;
    const top = categories.slice(0, COLLAPSED_COUNT);
    const selected = categories.find((c) => c.name === value);
    if (selected && !top.includes(selected)) return [...top, selected];
    return top;
  }, [categories, expanded, value]);

  const hidden = categories.length - visible.length;

  if (!phone) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 bg-background" data-testid="txn-category-select">
          <SelectValue placeholder="Select category" />
        </SelectTrigger>
        <SelectContent className="bg-card border-border max-h-64">
          {categories.map((c) => (
            <SelectItem key={c.id} value={c.name} data-testid={`category-option-${c.name}`}>
              <span className="flex items-center gap-2">
                <CategoryIcon category={c} size={15} />
                {c.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className="mt-1">
      <div
        role="radiogroup"
        aria-label={`${kind} category`}
        className="grid grid-cols-3 gap-2"
        data-testid="txn-category-grid"
      >
        {visible.map((c) => {
          const selected = c.name === value;
          return (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(c.name)}
              data-testid={`category-option-${c.name}`}
              className={`flex min-h-[4.5rem] flex-col items-center justify-center gap-1.5 rounded-md border px-1 py-2 text-center transition-colors ${
                selected
                  ? "border-transparent bg-secondary ring-2"
                  : "border-border bg-background active:bg-secondary/60"
              }`}
              style={selected ? { "--tw-ring-color": c.color, color: c.color } : undefined}
            >
              <CategoryIcon category={c} size={20} />
              <span className={`w-full truncate text-[11px] leading-tight ${selected ? "font-semibold" : "text-foreground"}`}>
                {c.name}
              </span>
            </button>
          );
        })}
      </div>

      {(hidden > 0 || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          data-testid="toggle-all-categories"
          className="mt-2 flex min-h-11 w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border text-xs font-medium text-muted-foreground active:bg-secondary/60"
        >
          {expanded ? (
            <>Show fewer <CaretUp size={12} weight="bold" /></>
          ) : (
            <>{hidden} more {hidden === 1 ? "category" : "categories"} <CaretDown size={12} weight="bold" /></>
          )}
        </button>
      )}
    </div>
  );
}
